import React from 'react';
import { 
  ChevronRight, 
  Pin, 
  X, 
  Target, 
  Clock, 
  RotateCcw, 
  Layers, 
  Flame, 
  Moon, 
  Sparkles, 
  Calendar, 
  Zap, 
  AlertCircle, 
  Brain, 
  Lightbulb,
  TrendingDown
} from 'lucide-react';
import suggestionsService from '../../services/suggestionsService';

export default function SmartInsights({ 
  smartSuggestions, 
  pinnedSuggestions, 
  suggestionsLoading, 
  insightsTab, 
  onTabChange,
  onSuggestionClick,
  onUnpin,
  userId
}) {
  return (
    <div className="bg-white rounded-[22px] overflow-hidden" style={{ boxShadow: '0 2px 6px rgba(0,0,0,0.06)' }}>
      {/* Tabs */}
      <div className="flex border-b" style={{ borderColor: '#F0F0F0' }}>
        <button
          onClick={() => onTabChange('insights')}
          className={`flex-1 py-3 text-[13px] font-semibold transition-colors ${
            insightsTab === 'insights' 
              ? 'text-[#7C3AED] border-b-2 border-[#7C3AED]' 
              : 'text-[#555555] hover:text-[#111111] hover:bg-[#F7F7F8]'
          }`}
          style={insightsTab === 'insights' ? {} : { backgroundColor: 'transparent' }}
        >
          Smart Insights
        </button>
        <button
          onClick={() => onTabChange('pinned')}
          className={`flex-1 py-3 text-[13px] font-semibold transition-colors flex items-center justify-center gap-1.5 ${
            insightsTab === 'pinned' 
              ? 'text-[#7C3AED] border-b-2 border-[#7C3AED]' 
              : 'text-[#555555] hover:text-[#111111] hover:bg-[#F7F7F8]'
          }`}
        >
          <Pin size={13} />
          Pinned
          {pinnedSuggestions.length > 0 && (
            <span className="ml-1 px-2 py-0.5 text-[10px] rounded-full" style={{ backgroundColor: 'rgba(136, 92, 255, 0.1)', color: '#7C3AED' }}>
              {pinnedSuggestions.length}
            </span>
          )}
        </button>
      </div>
      
      {/* Tab Content */}
      <div className="p-6">
        {insightsTab === 'insights' ? (
          // Insights Tab
          suggestionsLoading ? (
            <div className="space-y-3">
              {[1, 2].map(i => (
                <div key={i} className="flex items-center gap-3 p-3 bg-[#F7F7F8] rounded-[20px] animate-pulse" style={{ boxShadow: '0 2px 6px rgba(0,0,0,0.06)' }}>
                  <div className="w-10 h-10 rounded-full" style={{ backgroundColor: '#E0E0E0' }} />
                  <div className="flex-1 space-y-2">
                    <div className="h-3 rounded w-2/3" style={{ backgroundColor: '#E0E0E0' }} />
                    <div className="h-2.5 rounded w-4/5" style={{ backgroundColor: '#E0E0E0' }} />
                  </div>
                </div>
              ))}
            </div>
          ) : smartSuggestions.length > 0 ? (
            <div className="space-y-3">
              {/* Always show 3 cards: top 2 insights + AI Predictions */}
              {smartSuggestions.slice(0, 3).map((suggestion, idx) => {
                const typeId = typeof suggestion.type === 'string' ? suggestion.type : (suggestion.type?.id || 'none');
                
                // All icons use Swirl Purple
                const iconConfig = {
                  project_forming: { icon: Target, color: '#7C3AED' },
                  daily_rhythm: { icon: Clock, color: '#7C3AED' },
                  theme_comeback: { icon: RotateCcw, color: '#7C3AED' },
                  deep_dive: { icon: Layers, color: '#7C3AED' },
                  streak_momentum: { icon: Flame, color: '#7C3AED' },
                  time_pattern: { icon: Moon, color: '#7C3AED' },
                  early_signs: { icon: Sparkles, color: '#7C3AED' },
                  planning_signal: { icon: Calendar, color: '#7C3AED' },
                  content_spark: { icon: Zap, color: '#7C3AED' },
                  pattern_block: { icon: AlertCircle, color: '#7C3AED' },
                  ai_predictions: { icon: Brain, color: '#7C3AED' },
                  evening_pattern: { icon: Moon, color: '#7C3AED' },
                  emerging_direction: { icon: Sparkles, color: '#7C3AED' },
                  neglected_focus: { icon: Clock, color: '#7C3AED' },
                  time_gaps: { icon: TrendingDown, color: '#7C3AED' },
                };
                const config = iconConfig[typeId] || { icon: Lightbulb, color: '#7C3AED' };
                const IconComponent = config.icon;
                
                return (
                  <div
                    key={suggestion.id || idx}
                    onClick={() => onSuggestionClick(suggestion)}
                    className="group flex items-center gap-3 p-3 bg-white hover:bg-[#F7F7F8] rounded-[20px] cursor-pointer transition-all duration-200"
                    style={{ boxShadow: '0 2px 6px rgba(0,0,0,0.06)' }}
                  >
                    <div 
                      className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: 'rgba(136, 92, 255, 0.06)' }}
                    >
                      <IconComponent size={18} style={{ color: config.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-semibold" style={{ color: '#111111' }}>
                        {suggestion.title || suggestion.shortTitle}
                      </p>
                      <p className="text-[12px] line-clamp-3 mt-1 leading-relaxed" style={{ color: '#555555' }}>
                        {suggestion.subtitle || suggestion.description || suggestion.whyShowing || suggestion.reason || 'Based on your recent activity patterns and engagement'}
                      </p>
                    </div>
                    <ChevronRight size={14} className="text-gray-400 group-hover:text-[#7C3AED] flex-shrink-0 transition-colors" />
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-6">
              <div 
                className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3"
                style={{ backgroundColor: 'rgba(136, 92, 255, 0.06)' }}
              >
                <Brain size={20} style={{ color: '#7C3AED' }} />
              </div>
              <p className="text-[12px] font-medium" style={{ color: '#555555' }}>
                Loading insights...
              </p>
            </div>
          )
        ) : (
          // Pinned Tab
          pinnedSuggestions.length > 0 ? (
            <div className="space-y-3">
              {pinnedSuggestions.map((pinned, idx) => {
                const typeId = typeof pinned.type === 'string' ? pinned.type : (pinned.type?.id || 'none');
                
                const iconConfig = {
                  project_forming: { icon: Target, color: '#7C3AED' },
                  daily_rhythm: { icon: Clock, color: '#7C3AED' },
                  theme_comeback: { icon: RotateCcw, color: '#7C3AED' },
                  deep_dive: { icon: Layers, color: '#7C3AED' },
                  streak_momentum: { icon: Flame, color: '#7C3AED' },
                  time_pattern: { icon: Moon, color: '#7C3AED' },
                  early_signs: { icon: Sparkles, color: '#7C3AED' },
                  planning_signal: { icon: Calendar, color: '#7C3AED' },
                  content_spark: { icon: Zap, color: '#7C3AED' },
                  pattern_block: { icon: AlertCircle, color: '#7C3AED' },
                  ai_predictions: { icon: Brain, color: '#7C3AED' },
                  evening_pattern: { icon: Moon, color: '#7C3AED' },
                  emerging_direction: { icon: Sparkles, color: '#7C3AED' },
                  neglected_focus: { icon: Clock, color: '#7C3AED' },
                  time_gaps: { icon: TrendingDown, color: '#7C3AED' },
                };
                const config = iconConfig[typeId] || { icon: Lightbulb, color: '#7C3AED' };
                const IconComponent = config.icon;
                
                return (
                  <div
                    key={pinned.id || idx}
                    className="group flex items-center gap-3 p-3 bg-white hover:bg-[#F7F7F8] rounded-[20px]"
                    style={{ boxShadow: '0 2px 6px rgba(0,0,0,0.06)' }}
                  >
                    <div 
                      onClick={() => onSuggestionClick(pinned)}
                      className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer"
                    >
                      <div 
                        className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: 'rgba(136, 92, 255, 0.06)' }}
                      >
                        <IconComponent size={18} style={{ color: config.color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[13px] font-semibold" style={{ color: '#111111' }}>
                          {pinned.title || pinned.shortTitle}
                        </p>
                        <p className="text-[12px] line-clamp-3 mt-1 leading-relaxed" style={{ color: '#555555' }}>
                          {pinned.subtitle || pinned.description || pinned.whyShowing || pinned.reason || 'Based on your recent activity patterns and engagement'}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={async () => {
                        const newPinned = await suggestionsService.unpinSuggestion(pinned.id, userId);
                        onUnpin(newPinned);
                      }}
                      className="p-1.5 hover:bg-white rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                      style={{ color: '#999999' }}
                    >
                      <X size={12} />
                    </button>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-6">
              <div 
                className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3"
                style={{ backgroundColor: 'rgba(136, 92, 255, 0.06)' }}
              >
                <Pin size={20} style={{ color: '#999999' }} />
              </div>
              <p className="text-[12px]" style={{ color: '#999999' }}>
                Pin insights to save them here.
              </p>
            </div>
          )
        )}
      </div>
    </div>
  );
}
